/**
 * Financiar → Rapoarte: raportul financiar al antreprenorului.
 *
 * O perioadă la alegere — lună, trimestru, an sau interval liber — cu baza de
 * comparație selectabilă; firmele se filtrează ca pe pagina Financiar.
 * „Închide luna" îngheață cifrele (doar pentru o lună, fiindcă doar luna se
 * închide), iar „Descarcă PDF" / „Descarcă Excel" exportă exact perioada
 * afișată.
 *
 * Un singur request aduce tot raportul, deci taburile sunt pur de prezentare:
 * nu se mai cere nimic de la server la schimbarea tabului, și un snapshot
 * închis nu poate fi parțial.
 */
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Download, FileSpreadsheet, Lock, LockOpen, RefreshCw } from 'lucide-react';
import {
  useFinancialReport,
  useMonthlyReportClosing,
  useMonthlyReportClosings,
} from '../hooks/useMonthlyReport';
import {
  REPORT_VERSION,
  downloadReportPdf,
  downloadReportXlsx,
  getFinancialReport,
  type MonthlyReport,
} from '../lib/monthlyReportApi';
import { fmtDate } from '../lib/monthlyReportFormat';
import {
  periodFromParams,
  periodTitle,
  periodToParams,
  type ReportPeriodState,
} from '../lib/reportPeriod';
import { CompanyMultiSelect } from '../components/financials/CompanyMultiSelect';
import { ReportPeriodBar } from '../components/financials/monthlyReport/ReportPeriodBar';
import {
  AccountsSection,
  DataQualitySection,
  ExpensesSection,
  OpenInvoicesSection,
  ProjectsSection,
  RevenueSection,
  VatSection,
} from '../components/financials/monthlyReport/ReportSections';
import { TrendsSection } from '../components/financials/monthlyReport/TrendsSection';
import { SynthesisSection } from '../components/financials/monthlyReport/SynthesisSection';
import { PeopleSection } from '../components/financials/monthlyReport/PeopleSection';
import { ClientsSection } from '../components/financials/monthlyReport/ClientsSection';
import { FadeIn, SkeletonStats, SkeletonTable } from '../components/ui/Skeleton';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { useActiveCompanyId } from '../store/useCompanyStore';
import { useAuthStore } from '../store/useAuthStore';
import { useI18n } from '../contexts/I18nContext';
import { useActiveTabInView } from '../hooks/useActiveTabInView';
import type { TranslationKey } from '../lib/i18n';

const TABS = [
  'synthesis',
  'revenue',
  'expenses',
  'people',
  'projects',
  'cash',
  'tax',
  'quality',
] as const;
type ReportTab = (typeof TABS)[number];

/** Parametrii de URL pe care îi stăpânește selectorul de perioadă. */
const PERIOD_PARAM_KEYS = ['kind', 'cmp', 'y', 'm', 'q', 'from', 'to'] as const;

function tabFromParams(params: URLSearchParams): ReportTab {
  const raw = params.get('tab');
  return TABS.includes(raw as ReportTab) ? (raw as ReportTab) : 'synthesis';
}

export function MonthlyReportPage() {
  const { t, locale } = useI18n();
  const confirm = useConfirm();
  const [params, setParams] = useSearchParams();
  const [period, setPeriod] = useState<ReportPeriodState>(() => periodFromParams(params));
  const [tab, setTab] = useState<ReportTab>(() => tabFromParams(params));
  const activeCompanyId = useActiveCompanyId();
  const [companyIds, setCompanyIds] = useState<string[]>(activeCompanyId ? [activeCompanyId] : []);
  const [exporting, setExporting] = useState<'pdf' | 'xlsx' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [recomputing, setRecomputing] = useState(false);
  // Raportul recalculat manual peste un snapshot vechi; trăiește cât pagina.
  const [liveOverride, setLiveOverride] = useState<MonthlyReport | null>(null);
  const canClose = useAuthStore((s) => s.user?.role === 'ADMIN' || s.user?.role === 'MANAGER');

  const scope = companyIds.length > 0 ? companyIds : undefined;
  // Un interval inversat nu se trimite la server; bara afișează mesajul.
  const invalidRange = period.kind === 'custom' && period.to < period.from;
  const query = useFinancialReport(period, scope, !invalidRange);
  const closingsQuery = useMonthlyReportClosings(scope);
  // Închiderea rămâne pe lună: anchor-ul e luna selectată.
  const { close, reopen } = useMonthlyReportClosing(period.year, period.month, scope, period);

  // Perioada și tabul stau în URL, ca raportul să fie linkabil și să supravie-
  // țuiască unui refresh.
  useEffect(() => {
    const periodParams = periodToParams(period);
    const next = new URLSearchParams(params);
    for (const key of PERIOD_PARAM_KEYS) {
      const value = periodParams[key];
      if (value === undefined) next.delete(key);
      else next.set(key, value);
    }
    next.set('tab', tab);
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [period, tab, params, setParams]);

  // Orice schimbare de perioadă sau de firme invalidează recalcularea manuală.
  useEffect(() => {
    setLiveOverride(null);
  }, [period, companyIds]);

  const now = new Date();
  const isCurrentOrFuture =
    period.year > now.getFullYear() ||
    (period.year === now.getFullYear() && period.month >= now.getMonth() + 1);
  const canCloseThisPeriod = canClose && period.kind === 'month';
  const busy = close.isPending || reopen.isPending;

  async function handleClose() {
    setActionError(null);
    const ok = await confirm({
      title: t('monthlyReport.closeMonth'),
      message: t('monthlyReport.closeConfirm', { period: periodTitle(period, locale) }),
      confirmLabel: t('monthlyReport.closeMonth'),
    });
    if (!ok) return;
    try {
      await close.mutateAsync();
    } catch {
      setActionError(t('monthlyReport.actionFailed'));
    }
  }

  async function handleReopen() {
    setActionError(null);
    const ok = await confirm({
      title: t('monthlyReport.reopenMonth'),
      message: t('monthlyReport.reopenConfirm', { period: periodTitle(period, locale) }),
      confirmLabel: t('monthlyReport.reopenMonth'),
      danger: true,
    });
    if (!ok) return;
    try {
      await reopen.mutateAsync();
    } catch {
      setActionError(t('monthlyReport.actionFailed'));
    }
  }

  async function handleExport(format: 'pdf' | 'xlsx') {
    setActionError(null);
    setExporting(format);
    try {
      await (format === 'pdf'
        ? downloadReportPdf(period, scope)
        : downloadReportXlsx(period, scope));
    } catch {
      setActionError(t('monthlyReport.actionFailed'));
    } finally {
      setExporting(null);
    }
  }

  /** Recalculează un snapshot vechi, fără să-l rescrie: doar îl afișează live. */
  async function handleRecompute() {
    setActionError(null);
    setRecomputing(true);
    try {
      const fresh = await getFinancialReport(period, scope, true);
      setLiveOverride(fresh.report);
    } catch {
      setActionError(t('monthlyReport.actionFailed'));
    } finally {
      setRecomputing(false);
    }
  }

  const data = query.data;
  const report = liveOverride ?? data?.report;
  const tabsRef = useActiveTabInView<HTMLDivElement>(tab, Boolean(report));
  const closing = data?.closing;
  const staleSnapshot =
    !!report && !!data?.fromSnapshot && (report.reportVersion ?? 0) < REPORT_VERSION;
  const bucketKind = report?.trends.bucketKind ?? 'month';

  const tabLabels = useMemo(
    () =>
      TABS.map((key) => ({
        key,
        label: t(`monthlyReport.tab.${key}` as TranslationKey),
      })),
    [t],
  );

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">
            {period.kind === 'month' ? t('monthlyReport.title') : t('monthlyReport.titleGeneric')} ·{' '}
            {periodTitle(period, locale)}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {report?.periodInfo
              ? t('monthlyReport.period.comparedWith', {
                  period: report.periodInfo.compare.key,
                })
              : t('monthlyReport.subtitle')}
          </p>
        </div>
        {/* Phones: firm picker across the row, PDF / Excel as two equal
            halves, close / reopen across the row; not a ragged wrap. */}
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap sm:items-center">
          <CompanyMultiSelect
            value={companyIds}
            onChange={setCompanyIds}
            className="col-span-2 w-full sm:w-52"
          />
          <button
            type="button"
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null || !report}
            className="inline-flex items-center justify-center gap-1.5 rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            {t('monthlyReport.downloadPdf')}
          </button>
          <button
            type="button"
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null || !report}
            className="inline-flex items-center justify-center gap-1.5 rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <FileSpreadsheet className="h-4 w-4" />
            {t('monthlyReport.downloadXlsx')}
          </button>
          {canCloseThisPeriod && closing && !closing.closed && (
            <button
              type="button"
              onClick={handleClose}
              disabled={busy || isCurrentOrFuture}
              title={isCurrentOrFuture ? t('monthlyReport.open') : undefined}
              className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              <Lock className="h-4 w-4" />
              {t('monthlyReport.closeMonth')}
            </button>
          )}
          {canCloseThisPeriod && closing?.closed && (
            <button
              type="button"
              onClick={handleReopen}
              disabled={busy}
              className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <LockOpen className="h-4 w-4" />
              {t('monthlyReport.reopenMonth')}
            </button>
          )}
        </div>
      </div>

      <ReportPeriodBar
        value={period}
        onChange={setPeriod}
        closings={closingsQuery.data ?? []}
        invalidRange={invalidRange}
      />

      {closing && period.kind === 'month' && (
        <div className="mt-3">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${
              closing.closed
                ? 'border-green-200 bg-green-50 text-green-700'
                : 'border-gray-200 bg-gray-50 text-gray-600'
            }`}
          >
            {closing.closed ? (
              <Lock className="h-3.5 w-3.5" />
            ) : (
              <LockOpen className="h-3.5 w-3.5" />
            )}
            {closing.closed
              ? closing.closedBy
                ? t('monthlyReport.closedAtBy', {
                    date: fmtDate(closing.closedAt, locale),
                    name: closing.closedBy.name,
                  })
                : t('monthlyReport.closedAt', { date: fmtDate(closing.closedAt, locale) })
              : t('monthlyReport.open')}
          </span>
        </div>
      )}

      {actionError && <p className="mt-3 text-sm text-red-600">{actionError}</p>}
      {query.isError && <p className="mt-3 text-sm text-red-600">{t('monthlyReport.loadError')}</p>}

      {query.isLoading && (
        <div className="mt-6 space-y-6">
          <SkeletonStats />
          <SkeletonTable rows={4} columns={5} />
          <SkeletonTable rows={6} columns={4} />
        </div>
      )}

      {report && (
        <FadeIn>
          {closing?.closed && data?.fromSnapshot && !liveOverride && (
            <p className="mt-4 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
              {t('monthlyReport.snapshotNote')}
            </p>
          )}
          {staleSnapshot && (
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <span>{t('monthlyReport.staleSnapshot')}</span>
              <button
                type="button"
                onClick={handleRecompute}
                disabled={recomputing}
                className="inline-flex items-center gap-1.5 rounded border border-amber-300 bg-white px-2 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${recomputing ? 'animate-spin' : ''}`} />
                {t('monthlyReport.recompute')}
              </button>
            </div>
          )}

          {/* Taburi: tot raportul e deja în memorie, deci schimbarea e instantanee. */}
          <div
            ref={tabsRef}
            role="tablist"
            aria-label={t('monthlyReport.title')}
            className="relative mt-6 flex gap-1 overflow-x-auto border-b border-gray-200"
          >
            {tabLabels.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={`-mb-px shrink-0 border-b-2 px-3 py-2 text-sm font-medium ${
                  tab === key
                    ? 'border-brand-600 text-brand-700'
                    : 'border-transparent text-gray-600 hover:border-gray-300 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {tab === 'synthesis' && <SynthesisSection report={report} />}

          {tab === 'revenue' && (
            <>
              <RevenueSection revenue={report.revenue} />
              {report.clients && (
                <ClientsSection clients={report.clients} bucketKind={bucketKind} />
              )}
              <OpenInvoicesSection kind="receivables" data={report.receivables} />
            </>
          )}

          {tab === 'expenses' && (
            <>
              <ExpensesSection expenses={report.expenses} />
              <OpenInvoicesSection kind="payables" data={report.payables} />
            </>
          )}

          {tab === 'people' &&
            (report.people ? (
              <PeopleSection people={report.people} bucketKind={bucketKind} />
            ) : (
              <p className="mt-6 text-sm text-gray-500">{t('monthlyReport.staleSnapshot')}</p>
            ))}

          {tab === 'projects' && <ProjectsSection projects={report.projects} />}

          {tab === 'cash' && (
            <>
              <AccountsSection accounts={report.accounts} />
              <TrendsSection trends={report.trends} />
            </>
          )}

          {tab === 'tax' && <VatSection vat={report.vat} />}

          {tab === 'quality' && <DataQualitySection quality={report.dataQuality} />}

          <p className="mt-4 text-xs text-gray-400">
            {t('monthlyReport.generatedAt', { date: fmtDate(report.generatedAt, locale) })}
          </p>
        </FadeIn>
      )}
    </div>
  );
}
